//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { SamplingCampaignRequest } from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import { usePurchaseOptions } from '@campaigns/components/shared/purchase-options/usePurchaseOptions';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { getPurchaseOptionProductTypes } from '@campaigns/utils/sampling/SamplingCampaignRequestUtils';
import { BodyText, CaptionSecondaryText } from '@components/atoms/text/Text';
import { RadioGroup } from '@components/primitives/radio-group/RadioGroup';
import { pluralize } from '@utils/stringUtils';

export enum PrepaidContinueOption {
  RequestNewPurchase = 'requestNewPurchase',
  ChangePurchaseOption = 'changePurchaseOption',
}

interface PrepaidSubmissionOptionsProps {
  selectedOption: PrepaidContinueOption | undefined;
  onOptionChange: (option: PrepaidContinueOption) => void;
}

const RADIO_ITEM_ID = {
  requestNewPurchase: 'prepaid-continue-request-new-purchase',
  changePurchaseOption: 'prepaid-continue-change-purchase-option',
} as const;

export const PrepaidSubmissionOptions = ({
  onOptionChange,
  selectedOption,
}: PrepaidSubmissionOptionsProps) => {
  const { control } = useFormContext<SamplingCampaignRequest>();
  const type = useWatch({ control, name: 'type' });
  const communityType = useWatch({ control, name: 'communityType' });
  const { purchaseOptions } = usePurchaseOptions({
    productTypes: getPurchaseOptionProductTypes(type, communityType),
  });
  const activePurchaseOptionCount = purchaseOptions.length;

  return (
    <StyledRadioGroup
      onValueChange={(value) => onOptionChange(value as PrepaidContinueOption)}
      value={selectedOption}
    >
      <RadioGroupItemWrapper>
        <RadioContentWrapper>
          <StyledRadioGroupItem
            id={RADIO_ITEM_ID.changePurchaseOption}
            trackingId={
              SAMPLING_CAMPAIGNS_TRACKING_ID.PREPAID_MISMATCH_CHANGE_PURCHASE_OPTION_RADIO
            }
            value={PrepaidContinueOption.ChangePurchaseOption}
          >
            <RadioGroup.Indicator />
          </StyledRadioGroupItem>
          <RadioLabelColumn>
            <StyledRadioGroupLabel htmlFor={RADIO_ITEM_ID.changePurchaseOption}>
              <BodyText>Apply a different purchase option</BodyText>
            </StyledRadioGroupLabel>
            <CaptionSecondaryText>
              {`You have ${pluralize(activePurchaseOptionCount, 'active purchase')} in your account`}
            </CaptionSecondaryText>
          </RadioLabelColumn>
        </RadioContentWrapper>
      </RadioGroupItemWrapper>
      <RadioGroupItemWrapper>
        <RadioContentWrapper>
          <StyledRadioGroupItem
            id={RADIO_ITEM_ID.requestNewPurchase}
            trackingId={
              SAMPLING_CAMPAIGNS_TRACKING_ID.PREPAID_MISMATCH_REQUEST_NEW_PURCHASE_RADIO
            }
            value={PrepaidContinueOption.RequestNewPurchase}
          >
            <RadioGroup.Indicator />
          </StyledRadioGroupItem>
          <RadioLabelColumn>
            <StyledRadioGroupLabel htmlFor={RADIO_ITEM_ID.requestNewPurchase}>
              <BodyText>Request a new purchase</BodyText>
            </StyledRadioGroupLabel>
            <CaptionSecondaryText>
              Request this campaign as a new purchase, not using your existing
              purchases
            </CaptionSecondaryText>
          </RadioLabelColumn>
        </RadioContentWrapper>
      </RadioGroupItemWrapper>
    </StyledRadioGroup>
  );
};

const StyledRadioGroup = styled(RadioGroup)(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings[2]};
`,
);

const RadioGroupItemWrapper = styled.div`
  display: flex;
  align-items: flex-start;
`;

const RadioContentWrapper = styled.div`
  display: flex;
  align-items: flex-start;
`;

const StyledRadioGroupItem = styled(RadioGroup.Item)`
  margin-right: ${({ theme }) => theme.spacings[1]};
  margin-top: ${({ theme }) => theme.spacings[1]};
`;

const StyledRadioGroupLabel = styled(RadioGroup.Label)`
  display: flex;
  align-items: center;
`;

const RadioLabelColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[1]};
`;
